/**
 * @fileoverview Deposits Portfolio — Deposit Averages & Productivity page.
 * @description Ported from the InsightBox Banking design system's own
 * `DepositsPortfolio/DepositAverages.tsx`, including its two bespoke local card types (a
 * per-deposit-type card, a flow/productivity stat card) — both genuinely page-specific, not
 * reusable shapes, so they stay local components here rather than becoming new shared molecules.
 * Icons inlined to match this app's own icon convention instead of `lucide-react`.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useCurrentSession } from '@/features/auth'
import {
  ALL_BRANCHES_ID,
  BranchFilterSelect,
  FIVE_YEAR_ID,
  FyFilterSelect,
  LastUpdatedLabel,
  branchLabel,
  depositAverages,
  formatCr,
  formatNumber,
  formatRupee,
  formatSignedPct,
  prevFY,
  resolveBankingAccessScope,
  resolveFy,
  trendLabel,
  useBankingData,
} from '@/features/banking'
import BankingKpiStrip from '@/shared/components/molecules/BankingKpiStrip'
import BusinessSummaryPanel from '@/shared/components/molecules/BusinessSummaryPanel'
import ChartCard from '@/shared/components/molecules/ChartCard'
import PageHeader from '@/shared/components/molecules/PageHeader'
import {
  CHART_ACCENT,
  CHART_PRIMARY,
  CHART_PRIMARY_LIGHT,
} from '@/shared/constants/chart-theme.constants'

import type { FyFilter } from '@/features/banking'
import type { BankingKpiStripItem } from '@/shared/components/molecules/BankingKpiStrip'
import type { ReactNode } from 'react'

/**
 * @description Green rising arrow / red falling arrow, spelling out "YoY" explicitly rather than a
 * bare percentage so the comparison (vs the prior fiscal year) is never left implicit. Used on
 * every card on this page.
 */
function TrendBadge({ value }: { value: number }) {
  const up = value >= 0
  return (
    <span
      className={`inline-flex shrink-0 items-center gap-0.5 text-xs font-semibold ${up ? 'text-success' : 'text-danger'}`}
    >
      <svg
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d={up ? 'M12 19V5M5 12l7-7 7 7' : 'M12 5v14M5 12l7 7 7-7'} />
      </svg>
      {formatSignedPct(value)} YoY
    </span>
  )
}

interface TypeCardProps {
  color: string
  label: string
  value: string
  count: number
  yoy: number | null
  accountsLabel: string
}

function TypeCard({ color, label, value, count, yoy, accountsLabel }: TypeCardProps) {
  return (
    <div className="flex h-full flex-col justify-center gap-1.5 rounded-2xl border border-border bg-input-bg p-5">
      <div className="flex items-center gap-2">
        <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: color }} />
        <p className="text-xs font-semibold tracking-wide text-label uppercase">{label}</p>
      </div>
      <p className="text-2xl font-extrabold text-text">{value}</p>
      <p className="flex flex-wrap items-center gap-x-2 text-xs text-label">
        <span>
          {accountsLabel} · {formatNumber(count)}
        </span>
        {yoy !== null && <TrendBadge value={yoy} />}
      </p>
    </div>
  )
}

interface FlowStatProps {
  icon: ReactNode
  label: string
  value: string
  yoy: number | null
}

// Icon color stays uniform brand-blue — this app reserves green/amber/red for actual good/bad
// signals, not decoration. The YoY badge below is a real signal, so it still earns that color; the
// icon wrapper doesn't.
function FlowStat({ icon, label, value, yoy }: FlowStatProps) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-1.5 rounded-2xl bg-input-bg px-4 py-3.5 text-center">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-light text-primary">
        {icon}
      </span>
      <p className="text-[11px] font-semibold tracking-wide text-label uppercase">{label}</p>
      <p className="text-xl font-bold text-text">{value}</p>
      {yoy !== null && <TrendBadge value={yoy} />}
    </div>
  )
}

const ICON_PROPS = {
  width: 18,
  height: 18,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2.2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
}

const ICON_USERS = (
  <svg {...ICON_PROPS}>
    <circle cx="9" cy="8" r="3.2" />
    <path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6" />
    <circle cx="17" cy="9" r="2.6" />
    <path d="M15.5 14.2c2.5.5 4.5 2.7 4.5 5.8" />
  </svg>
)
const ICON_LANDMARK = (
  <svg {...ICON_PROPS}>
    <path d="M4 21h16M5 21V10M9 21V10M15 21V10M19 21V10M3 10l9-6 9 6" />
  </svg>
)
const ICON_COINS = (
  <svg {...ICON_PROPS}>
    <ellipse cx="9" cy="7" rx="6" ry="3" />
    <path d="M3 7v10c0 1.7 2.7 3 6 3s6-1.3 6-3V7" />
    <path d="M15 9.5c2.9.3 6 1.4 6 3v6.5c0 1.7-3.1 3-6 3-1.6 0-3-.3-4-.9" />
  </svg>
)
const ICON_USER_COG = (
  <svg {...ICON_PROPS}>
    <circle cx="10" cy="8" r="3.2" />
    <path d="M4 20c0-3.3 2.7-6 6-6s6 2.7 6 6" />
    <circle cx="19" cy="15" r="1.6" />
    <path d="M19 12v.8M19 16.2V17M16.6 13.5l.7.4M20.7 15.6l.7.4M16.6 16.5l.7-.4M20.7 14.4l.7-.4" />
  </svg>
)

/**
 * @component
 */
function BankingDepositsAveragesPage() {
  const { t } = useTranslation()
  const dataQuery = useBankingData()
  const [fyFilter, setFyFilter] = useState<FyFilter>(FIVE_YEAR_ID)
  const bankingScope = resolveBankingAccessScope(useCurrentSession().data?.bankingScope)
  const [branchId, setBranchId] = useState(bankingScope.defaultBranchId)

  const header = (
    <PageHeader
      kicker={t('banking.depositsAverages.kicker')}
      title={t('banking.depositsAverages.title')}
      description={t('banking.depositsAverages.description')}
      filters={
        <>
          <FyFilterSelect value={fyFilter} onChange={setFyFilter} />
          <BranchFilterSelect value={branchId} onChange={setBranchId} scope={bankingScope} />
          {dataQuery.data && <LastUpdatedLabel value={dataQuery.data.meta.lastUpdated} />}
        </>
      }
    />
  )

  if (dataQuery.isPending) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <BankingKpiStrip
          loading
          items={[
            { label: '', value: '', highlight: true },
            { label: '', value: '' },
            { label: '', value: '' },
            { label: '', value: '' },
          ]}
        />
        <ChartCard title="" loading>
          <div />
        </ChartCard>
      </div>
    )
  }

  if (dataQuery.isError) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <p className="text-sm text-danger">{t('banking.loadError')}</p>
      </div>
    )
  }

  const data = dataQuery.data
  const fy = resolveFy(fyFilter)
  const d = depositAverages(data, fy, branchId)
  const bank = depositAverages(data, fy, ALL_BRANCHES_ID)
  const isAllBranches = branchId === ALL_BRANCHES_ID
  const skewPct = isAllBranches
    ? 0
    : ((d.avgPerAccount - bank.avgPerAccount) / bank.avgPerAccount) * 100
  const skewUp = skewPct >= 0

  // Every card on this page gets a YoY comparison — null (no badge shown) only for FY21, which has
  // no prior year to compare against.
  const priorFy = prevFY(fy)
  const prior = priorFy ? depositAverages(data, priorFy, branchId) : null
  const yoy = (current: number, previous: number | undefined): number | null =>
    prior && previous ? ((current - previous) / previous) * 100 : null

  const termYoY = yoy(d.avgTermPerAccount, prior?.avgTermPerAccount)
  const savingsYoY = yoy(d.avgSavingsPerAccount, prior?.avgSavingsPerAccount)
  const currentYoY = yoy(d.avgCurrentPerAccount, prior?.avgCurrentPerAccount)
  const avgPerAccountYoY = yoy(d.avgPerAccount, prior?.avgPerAccount)
  const employeesYoY = yoy(d.employees, prior?.employees)
  const totalAccountsYoY = yoy(d.totalAccounts, prior?.totalAccounts)
  const totalDepositsYoY = yoy(d.totalDepositsCr, prior?.totalDepositsCr)
  const accountsPerEmployeeYoY = yoy(d.accountsPerEmployee, prior?.accountsPerEmployee)

  const branchName = branchLabel(branchId)

  const kpis: BankingKpiStripItem[] = [
    {
      label: t('banking.depositsAverages.kpi.avgTerm'),
      value: formatRupee(d.avgTermPerAccount),
      sublabel:
        termYoY !== null
          ? t('banking.yoyTrend', { value: formatSignedPct(termYoY) })
          : t('banking.depositsAverages.kpi.duringTheYear'),
      highlight: true,
      ...(termYoY !== null && { trend: { direction: trendLabel(termYoY) } }),
    },
    {
      label: t('banking.depositsAverages.kpi.avgSavings'),
      value: formatRupee(d.avgSavingsPerAccount),
      sublabel:
        savingsYoY !== null
          ? t('banking.yoyTrend', { value: formatSignedPct(savingsYoY) })
          : t('banking.depositsAverages.kpi.duringTheYear'),
      ...(savingsYoY !== null && { trend: { direction: trendLabel(savingsYoY) } }),
    },
    {
      label: t('banking.depositsAverages.kpi.avgCurrent'),
      value: formatRupee(d.avgCurrentPerAccount),
      sublabel:
        currentYoY !== null
          ? t('banking.yoyTrend', { value: formatSignedPct(currentYoY) })
          : t('banking.depositsAverages.kpi.duringTheYear'),
      ...(currentYoY !== null && { trend: { direction: trendLabel(currentYoY) } }),
    },
    {
      label: t('banking.depositsAverages.kpi.avgPerAccount'),
      value: formatRupee(d.avgPerAccount),
      sublabel:
        avgPerAccountYoY !== null
          ? t('banking.yoyTrend', { value: formatSignedPct(avgPerAccountYoY) })
          : t('banking.depositsAverages.kpi.acrossAccounts', {
              count: formatNumber(d.totalAccounts),
            }),
      ...(avgPerAccountYoY !== null && {
        trend: { direction: trendLabel(avgPerAccountYoY) },
      }),
    },
  ]

  return (
    <div className="flex flex-col gap-4">
      {header}
      <BankingKpiStrip items={kpis} />

      <ChartCard
        fill
        title={t('banking.depositsAverages.chartTitle')}
        subtitle={t('banking.depositsAverages.chartSubtitle', {
          branch: branchLabel(branchId),
          fy,
        })}
      >
        {/* Two stacked rows, each flex-1 so they split the fill-mode ChartCard's height evenly. */}
        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto">
          <div className="grid min-h-0 flex-1 grid-cols-[repeat(auto-fit,minmax(min(160px,100%),1fr))] gap-4">
            <TypeCard
              color={CHART_ACCENT}
              label={t('banking.depositsAverages.byType.term')}
              value={formatRupee(d.avgTermPerAccount)}
              count={d.accounts.term}
              yoy={termYoY}
              accountsLabel={t('banking.depositsAverages.avgPerAccount')}
            />
            <TypeCard
              color={CHART_PRIMARY}
              label={t('banking.depositsAverages.byType.savings')}
              value={formatRupee(d.avgSavingsPerAccount)}
              count={d.accounts.savings}
              yoy={savingsYoY}
              accountsLabel={t('banking.depositsAverages.avgPerAccount')}
            />
            <TypeCard
              color={CHART_PRIMARY_LIGHT}
              label={t('banking.depositsAverages.byType.current')}
              value={formatRupee(d.avgCurrentPerAccount)}
              count={d.accounts.current}
              yoy={currentYoY}
              accountsLabel={t('banking.depositsAverages.avgPerAccount')}
            />
          </div>

          <div className="grid min-h-0 flex-1 grid-cols-[repeat(auto-fit,minmax(min(150px,100%),1fr))] gap-4">
            <FlowStat
              icon={ICON_USERS}
              label={t('banking.depositsAverages.flow.totalEmployees')}
              value={formatNumber(d.employees)}
              yoy={employeesYoY}
            />
            <FlowStat
              icon={ICON_LANDMARK}
              label={t('banking.depositsAverages.flow.totalAccounts')}
              value={formatNumber(d.totalAccounts)}
              yoy={totalAccountsYoY}
            />
            <FlowStat
              icon={ICON_COINS}
              label={t('banking.depositsAverages.flow.totalDeposits')}
              value={formatCr(d.totalDepositsCr)}
              yoy={totalDepositsYoY}
            />
            <FlowStat
              icon={ICON_USER_COG}
              label={t('banking.depositsAverages.flow.accountsPerEmployee')}
              value={formatNumber(d.accountsPerEmployee)}
              yoy={accountsPerEmployeeYoY}
            />
          </div>
        </div>
      </ChartCard>

      <BusinessSummaryPanel
        title={t('banking.businessSummaryAsOn', { fy })}
        lines={[
          t('banking.depositsAverages.summary.line1', {
            scope: isAllBranches ? t('banking.depositsAverages.summary.theBank') : branchName,
            accounts: formatNumber(d.totalAccounts),
            employees: formatNumber(d.employees),
            perEmployee: formatNumber(d.accountsPerEmployee),
          }),
          isAllBranches
            ? t('banking.depositsAverages.summary.line2AllBranches', {
                value: formatRupee(d.avgPerAccount),
              })
            : t('banking.depositsAverages.summary.line2Branch', {
                branch: branchName,
                value: formatRupee(d.avgPerAccount),
                skew: formatSignedPct(skewPct),
                bankValue: formatRupee(bank.avgPerAccount),
                direction: skewUp
                  ? t('banking.depositsAverages.summary.higherValueDepositors')
                  : t('banking.depositsAverages.summary.higherVolumeDepositors'),
              }),
        ]}
      />
    </div>
  )
}

export default BankingDepositsAveragesPage
